import Radium from '@flipdish/radium';
import axios from 'axios';
import { find, isEqual } from 'lodash';
import React from 'react';
import { FormGroup, ListGroupItem } from 'react-bootstrap';
import { findDOMNode } from 'react-dom';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { Alert, Grid, Hidden, PageTitle, Typography } from '../../../ui';
import * as Actions from '../../actions/Actions';
import { Track } from '../../analytics';
import * as Api from '../../Api';
import * as BasketControl from '../../BasketControl';
import { BLANK } from '../../constants/Constants';
import * as Data from '../../Data';
import { basketLogger } from '../../logger';
import * as OrderFlow from '../../OrderFlow';
import { basketActions, basketSelectors, userActions } from '../../redux';
import * as Routes from '../../Routes';
import { menuSelectors, paymentSelectors } from '../../selectors';
import * as Store from '../../Store';
import { EnsureVisible, NavigateTo } from '../../Utilities';
import LoadingPanel from '../LoadingPanel';
import PermissionConsentDialog from '../PermissionConsentDialog';
import Preorder from '../Preorder';
import Price from '../Price';
import RouterBackLink from '../RouterBackLink';
import TableSelectModal from '../TableSelectModal';
import TipOptions from '../TipOptions';
import { Toast } from '../Toast/index.weborder';
import UserNameLabelText from '../UserNameLabelText';
import PaymentSection from './components/PaymentSection';
import SelectTimeModal from './components/SelectTimeModal';
import Voucher from './components/Voucher';
import VoucherEditLink from './components/VoucherEditLink';

const tinycolor = require('tinycolor2');

const CHECKOUT_LOADING_STATES = ['loading', 'validating', 'submitting', 'cancelling'];

export interface SubmitTypeOptions {
  paypal?: {
    useSavedAccount?: boolean;
    nonce?: string;
    deviceData?: string;
  };
  googleApplePayStripeToken?: string;
}

interface CheckoutRoute
  extends RouteComponentProps<
    {
      vrid: string;
      type: 'collection' | 'delivery';
      id: string;
      concessionStoreId?: number;
    },
    {}
  > {}

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  Routes.Routes;

interface CustomChefNote {
  type: 'custom';
}

interface LoadedChefNote {
  type: 'loaded';
}

interface State {
  ChefNote: CustomChefNote | LoadedChefNote;
  PendingVouchers: any;
  ShowModal: boolean;
  ShowTimeAndNameModal: boolean;
}

class Checkout extends React.Component<Props, State> {
  constructor(props: Props, context: any) {
    super(props, context);
    this.state = {
      ChefNote: {
        type: 'loaded'
      },
      PendingVouchers: [],
      ShowModal: false,
      ShowTimeAndNameModal: false
    };
  }

  public componentDidMount() {
    const { Account, Basket, Customer, LoadRestaurant, Restaurant } = this.props;

    LoadRestaurant(this.props);
    EnsureVisible(findDOMNode(this));

    //If already available create the voucher
    if (
      Basket.items &&
      ((Basket.requestedTime !== BLANK && Basket.requestedTime !== '') ||
        !Restaurant ||
        !Restaurant.AcceptPreOrders) &&
      Customer &&
      Account
    ) {
      this.getVoucher(Account.PaymentAccountId, Basket);
    }

    this.loggedInCheck();
  }

  public componentWillUnmount() {
    const { Order, SetOrder } = this.props;

    if (Order.state === 'failed') {
      SetOrder({
        state: 'none',
        submitType: 'NORMAL'
      });
    }
  }

  // The customer might load after this component is first mounted.
  // We need to update the name field in this case.
  // Also we have to check whether the minimum basket value required for delivery is satisfied
  public UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { Basket, Customer, Account, Restaurant, VouchersInProgress, Order } = nextProps;

    const { PendingVouchers } = this.state;

    //If everything remains same, return
    if (
      isEqual(Basket.items, this.props.Basket.items) &&
      isEqual(Customer, this.props.Customer) &&
      isEqual(Account, this.props.Account) &&
      Restaurant === this.props.Restaurant &&
      isEqual(VouchersInProgress, this.props.VouchersInProgress) &&
      isEqual(nextProps.Basket.requestedTime, this.props.Basket.requestedTime)
    ) {
      return;
    }

    //If order already paid for, return
    if (Order.state === 'paid') {
      return;
    }

    //If order below minimum amount redirect to menu
    if (this.props.basketLoaded) {
      this.redirectToMenuScreenIfBelowMinimumOrderAmount(nextProps);
      this.redirectToMenuIfRemovedAllItemsInBasket(nextProps.Basket.items);
    }

    //If customer not loaded, return
    if (Customer.status !== 'success') {
      return;
    }

    //Get voucher when time changes or items in basket change
    if (
      Basket.requestedTime !== this.props.Basket.requestedTime ||
      !isEqual(Basket.items.length, this.props.Basket.items.length)
    ) {
      if (Account) {
        this.getVoucher(Account.PaymentAccountId, Basket);
      }
    }

    if (VouchersInProgress > this.props.VouchersInProgress) {
      const newPendingVouchers = PendingVouchers.slice(1);
      this.setState({ PendingVouchers: newPendingVouchers }, () => {
        basketLogger.debug('CHECKOUT PendingVouchers');
      });
    }
  }

  // TODO move this to 1 top component that handles global stuff like this
  private loggedInCheck = () => {
    const { SetCustomer } = this.props;

    const originalAlert = window.alert;
    window.alert = (text) => {
      console.log('tried to alert: ' + text);
      return true;
    };

    axios
      .get('/account/accountdetails')
      .then(() => {
        window.alert = originalAlert;

        this.props.loginSuccessBridge();
      })
      .catch(() => {
        window.alert = originalAlert;

        this.props.loginErrorBridge();

        SetCustomer({ status: 'none' });

        NavigateTo('/phone');
        axios.post('/Account/LogOff');
      });
  };

  private toggleModal = () => {
    if (this.state.ShowModal) {
      this.setState({ ShowModal: false });
    } else {
      this.setState({ ShowModal: true });
    }
  };

  private handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { EditTableNumbers, params } = this.props;

    EditTableNumbers(params.vrid, parseInt(e.target.value.replace(/\D/g, '')) || undefined);
  };

  // TODO move to saga
  // private changePaymentAccount = (account: Api.PaymentAccount) => {
  //   Track('Orders', 'Payment Method Selected');
  // };

  // If there is an exiting voucher apply the user
  private getVoucher = (paymentAccountId: number, basket: BasketControl.Data): any => {
    const { Restaurant, Basket, menu } = this.props;

    if (!menu) {
      return;
    }

    if (!Restaurant) {
      return;
    }

    const something = this.getOrder();
    if (!something) {
      return;
    }
    const { order } = something;

    const PreOrder = !Restaurant.IsOpen && Restaurant.AcceptPreOrders;
    if (PreOrder) {
      if (order.RequestedDeliveryOrPickupTimeUtc === BLANK) {
        return;
      }
    }

    const currentBasketTotal = BasketControl.Total(basket.items);
    const shouldCreateOrder =
      currentBasketTotal > Restaurant.MinimumOrder && order.OrderItemVms.length !== 0;

    // Create order with auto-apply voucher if no voucher applied
    if (shouldCreateOrder && basket.voucher.status === 'none') {
      basketLogger.debug('CHECKOUT CHECK AUTO-APPLY');
    } else if (shouldCreateOrder && basket.voucher.status === 'valid') {
      basketLogger.debug('CHECKOUT ApplyExitingVoucher');
    }
  };

  private changeDeliveryTime = (time: string) => {
    const { SetRequestedTime, menu } = this.props;

    if (!menu || time === 'blank') {
      // TODO:DN something here, should never happen but be warned
      return;
    }
    SetRequestedTime({ time, menuId: menu.MenuId });
  };

  private getRestaurantUrl = () => {
    const { type, vrid, id, concessionStoreId } = this.props.params;
    const restaurant =
      '/restaurant/' +
      vrid +
      '/' +
      type +
      '/' +
      id +
      (concessionStoreId ? '/' + concessionStoreId : '');
    return restaurant;
  };

  private redirectToMenuIfRemovedAllItemsInBasket = (basketItems: BasketControl.Item[]) => {
    if (!basketItems.length) {
      const { AddToast, translate } = this.props;
      const restuarantUrl = this.getRestaurantUrl();
      NavigateTo(restuarantUrl);
      AddToast(translate('Your_basket_is_empty') as string);
    }
  };

  private redirectToMenuScreenIfBelowMinimumOrderAmount = (nextProps: Props) => {
    const { Restaurant, AppConfig, Basket, Languages, AddToast, translate } = nextProps;

    const currentBasketTotal = BasketControl.Total(Basket.items);

    if (!Restaurant) {
      return;
    }
    //The checkout button wont be active when the restaurant isn't Loaded
    const { MinimumOrder, Currency } = Restaurant;

    let categoryNameTranslation = 'Restaurant_lowercase';

    if (AppConfig.status === 'success') {
      const { Category } = AppConfig.data;

      switch (Category) {
        case 0:
          categoryNameTranslation = 'Restaurant_lowercase';
          break;
        case 1:
          categoryNameTranslation = 'Cafe_lowercase';
          break;
        case 2:
          categoryNameTranslation = 'Store_lowercase';
          break;
        default:
          categoryNameTranslation = 'Restaurant_lowercase';
      }
    }

    let initialLang = 'en';

    for (const Language of Languages) {
      if (Language.active) {
        initialLang = Language.code;
        break;
      }
    }

    if (currentBasketTotal < MinimumOrder) {
      const restaurant = this.getRestaurantUrl();
      NavigateTo(restaurant);
      AddToast(
        translate('Minimum_order_checkout', {
          categoryName: translate(categoryNameTranslation) as string,
          amount: BasketControl.ToCurrency(MinimumOrder, Currency, initialLang)
        }) as string
      );
      return;
    }
  };

  private isTipablePaymentMethod = () => {
    const { Account } = this.props;

    return Account && Account.SupportsSubmitOrderTips;
  };

  private isTipEnabled = () => {
    const { AppConfig, Restaurant, Customer } = this.props;

    if (AppConfig.status !== 'success' || !Restaurant || Customer.status !== 'success') {
      return false;
    }

    return AppConfig.data.TipsEnabled && Restaurant.TipsEnabled && Customer.data.DisplayTipOption;
  };

  private getOrder = () => {
    const { Customer, Restaurant, Basket, orderPayload, orderTotalToPay } = this.props;

    const currency = (Restaurant && Restaurant.Currency) || Api.Currency.EUR;

    if (!orderPayload || typeof orderPayload === 'string') {
      return;
    }

    // if (this.accounts().length === 0) {
    //   const deliveryTime = this.getDeliveryTime(Basket.requestedTime);
    //   this.props.SetCardOrder({
    //     vrid: params.vrid,
    //     prid: params.id,
    //     chefNote: this.chefNote(),
    //     deliveryTime,
    //     type: params.type,
    //     id: params.id,
    //     currency,
    //     total: orderTotalToPay
    //   });
    //   NavigateTo(`/card/${encodeURIComponent(window.location.hash)}`);
    //   return false;
    // }

    const v = Basket.voucher;
    const voucher = v.status === 'valid' ? v.code : '';

    return {
      voucher,
      order: orderPayload,
      totalToPay: orderTotalToPay,
      currency
    };
  };

  private handleSubmit = () => {
    this.props.startCheckoutFlow();
  };

  private handleChefNoteChange = (event: React.FormEvent<any>) => {
    const { params, EditChefNotes } = this.props;

    Track('Orders', 'Add Note');

    const newValue: string = (event.target as any).value;
    this.setState({
      ChefNote: {
        type: 'custom'
      }
    });

    EditChefNotes(params.vrid, newValue);
  };

  private chefNote = (): string => {
    const { ChefNote } = this.state;

    const { ChefNotes, Restaurant } = this.props;

    switch (ChefNote.type) {
      case 'custom':
        return ChefNotes;
      case 'loaded':
        if (ChefNotes) {
          return ChefNotes;
        }
        break;
    }

    return (Restaurant && Restaurant.ChefNote) || '';
  };

  private computeDeliveryFee = (Restaurant: Props['Restaurant']): number => {
    if (!Restaurant) {
      return 0;
    }

    switch (Restaurant.DeliveryType) {
      case Api.DeliveryType.Delivery:
        return Restaurant.DeliveryFee;

      default:
        return 0;
    }
  };

  private trackVoucher = (): any => {
    Track('Orders', 'Add Voucher');
  };

  private renderVoucher = (currency: Api.Currency): JSX.Element | null => {
    const {
      AppDesignScheme,
      translate,
      PickupLocationType,
      Restaurant,
      Basket,
      TableNumber,
      isDark,
      params,
      Languages,
      orderPayload,
      Account
    } = this.props;

    const { type, vrid, id, concessionStoreId } = params;
    const paymentAccountId = Account && Account.PaymentAccountId;
    // const isStripe =
    //   Account &&
    //   Account.Type === 'StripePayButtonPaymentAccount';

    if (!type || !vrid || !id) {
      return null;
    }

    if (Basket && Basket.voucher.status === 'loading') {
      return (
        <Grid container justify="center">
          <Typography variant="body1" color="textSecondary">
            {translate('Loading_voucher')}...
          </Typography>
        </Grid>
      );
    }

    const linkProps = {
      disabled: false,
      tooltip: ''
    };
    if (typeof orderPayload === 'string') {
      switch (orderPayload) {
        case 'missing_pre_order_time': {
          linkProps.disabled = true;
          linkProps.tooltip = translate('Please_select_a_time_before_adding_voucher', {
            type: translate(type) as string
          }) as string;
          break;
        }
      }
    }
    //If payment method not selected
    // if (!paymentAccountId && !isStripe) {
    //   linkProps.disabled = true;
    //   linkProps.tooltip = translate('Please_select_a_payment_method') as string;
    // }
    //If table select only and table not selected
    if (
      PickupLocationType === Api.PickupLocationType.TableService &&
      typeof TableNumber !== 'number'
    ) {
      const TableServiceCategory = !Restaurant
        ? Api.TableServiceCategory.Generic
        : Restaurant.DeliveryType === Api.DeliveryType.Collection
        ? Restaurant.TableServiceCatagory || Api.TableServiceCategory.Generic
        : Api.TableServiceCategory.Generic;

      linkProps.disabled = true;
      linkProps.tooltip = translate(`Table_voucher_${TableServiceCategory}`) as string;
    }

    if (Basket.voucher.status === 'none') {
      return (
        <Grid container justify="center">
          <VoucherEditLink
            dataFd={'add_voucher_link'}
            vrId={vrid}
            type={type}
            id={id}
            paymentAccountId={paymentAccountId}
            concessionStoreId={concessionStoreId}
            onClick={this.trackVoucher}
            {...linkProps}
          >
            {translate('Add_voucher_code')}
          </VoucherEditLink>
        </Grid>
      );
    }

    const voucherItems = Basket.voucher.items.map((i, idx) => (
      <ListGroupItem key={idx} className="flex">
        <Typography color="textPrimary">
          {i.Name} {i.Description && `(${i.Description})`}
        </Typography>
        <Price
          Price={i.Amount}
          Currency={currency}
          Languages={Languages}
          AppDesignScheme={AppDesignScheme}
          isDark={isDark}
        />
      </ListGroupItem>
    ));

    return (
      <FormGroup key="voucher">
        <div className="checkout__items">
          <ListGroupItem className="flex" key="label">
            <Typography color="textPrimary" variant="subtitle2">
              {translate('Voucher')}
            </Typography>
          </ListGroupItem>

          {voucherItems}

          <ListGroupItem className="flex" key="label-change">
            <Grid container justify="center">
              <VoucherEditLink
                dataFd={'edit_voucher_link'}
                vrId={vrid}
                type={type}
                id={id}
                paymentAccountId={paymentAccountId}
                concessionStoreId={concessionStoreId}
                onClick={this.trackVoucher}
                {...linkProps}
              >
                {translate('Change_voucher')}
              </VoucherEditLink>
              <Hidden smDown>
                <Grid item xs />
              </Hidden>
            </Grid>
          </ListGroupItem>
        </div>
      </FormGroup>
    );
  };

  private validateName = (name: string) => {
    if (name) {
      const trimedName = name.trim();
      const isValidName = trimedName.length > 2;
      return isValidName;
    }
    return false;
  };

  public render(): JSX.Element {
    const {
      Restaurant,
      DeliveryLocation,
      AppConfig,
      PickupLocationType,
      AppDesignScheme,
      isDark,
      Customer,
      Languages,
      translate,
      Basket,
      params,
      Offset,
      TableNumber,
      EditPickupLocationTypes,
      RemoveItemFromBasket,
      AddItemToBasket,
      PercentageTip,
      orderTotalToPay,
      orderTipsTotal,
      orderFeesTotal,
      loading
    } = this.props;

    if (AppConfig.status != 'success' || !Restaurant) {
      return <LoadingPanel AppDesignScheme={AppDesignScheme} isDark={isDark} />;
    }

    const styles = {
      primary: {
        color: AppDesignScheme.Primary
      },
      secondary: {
        color: AppDesignScheme.Secondary
      },
      tertiary: {
        color: AppDesignScheme.Tertiary
      },
      tertiaryLight: {
        color: tinycolor(AppDesignScheme.Tertiary)
          .lighten(15)
          .toString()
      },
      nav: {
        ':hover': {},
        ':active': {}
      },
      secondaryActive: {
        color: tinycolor(AppDesignScheme.Secondary)
          .darken(10)
          .toString()
      },
      secondaryHover: {
        color: tinycolor(AppDesignScheme.Secondary)
          .darken(5)
          .toString()
      },
      subheader: {
        borderColor: AppDesignScheme.Secondary,
        boxShadow: `1px 1px 9px -5px ${AppDesignScheme.Secondary}`,
        backgroundColor: tinycolor(AppDesignScheme.Primary)
          .darken(3)
          .toString()
      },
      darkSubheader: {
        borderColor: tinycolor(AppDesignScheme.Tertiary)
          .darken(10)
          .toString(),
        backgroundColor: tinycolor(AppDesignScheme.Tertiary)
          .lighten(10)
          .toString(),
        boxShadow: `1px 1px 9px -5px ${tinycolor(AppDesignScheme.Tertiary).darken(10)}`
      },
      control: {
        color: AppDesignScheme.Tertiary,
        ':hover': {
          color: AppDesignScheme.Secondary
        },
        ':active': {
          color: tinycolor(AppDesignScheme.Secondary)
            .darken(10)
            .toString()
        }
      },
      darkControl: {
        color: AppDesignScheme.Primary,
        ':hover': {
          color: AppDesignScheme.Secondary
        },
        ':active': {
          color: tinycolor(AppDesignScheme.Secondary)
            .darken(10)
            .toString()
        }
      },
      price: {
        borderColor: tinycolor(AppDesignScheme.Primary)
          .darken(20)
          .toString()
      },
      hr: {
        borderColor: AppDesignScheme.Secondary
      },
      buttonPrimary: {
        backgroundColor: tinycolor(AppDesignScheme.Primary)
          .darken(3)
          .toString(),
        borderColor: AppDesignScheme.Secondary,
        ':hover': {
          borderColor: AppDesignScheme.Secondary
        },
        ':active': {},
        ':focus': {
          borderColor: AppDesignScheme.Secondary
        }
      },
      buttonSecondary: {
        backgroundColor: AppDesignScheme.Secondary,
        ':hover': {},
        ':active': {},
        ':focus': {}
      },
      buttonActive: {
        backgroundColor: tinycolor(AppDesignScheme.Secondary)
          .darken(10)
          .toString()
      },
      buttonHover: {
        backgroundColor: tinycolor(AppDesignScheme.Secondary)
          .darken(5)
          .toString()
      },
      buttonPrimaryHover: {
        backgroundColor: AppDesignScheme.Secondary
      },
      formControl: {
        color: AppDesignScheme.Tertiary,
        background: AppDesignScheme.Primary,
        borderColor: tinycolor(AppDesignScheme.Tertiary)
          .setAlpha(0.2)
          .toString(),
        ':focus': {
          borderColor: AppDesignScheme.Secondary
        }
      },
      darkFormControl: {
        color: AppDesignScheme.Primary,
        background: AppDesignScheme.Tertiary,
        borderColor: AppDesignScheme.Primary
      },
      primaryBackground: {
        background: AppDesignScheme.Primary
      },
      tertiaryBackground: {
        background: AppDesignScheme.Tertiary
      }
    };

    const currency = (Restaurant && Restaurant.Currency) || Api.Currency.EUR;

    let initialLang = 'en';

    for (const Language of Languages) {
      if (Language.active) {
        initialLang = Language.code;
        break;
      }
    }

    let deliveryFeeRow: JSX.Element | null = null;

    const deliveryFee: number = this.computeDeliveryFee(Restaurant);
    if (deliveryFee > 0) {
      deliveryFeeRow = (
        <ListGroupItem className="flex">
          <p
            tabIndex={0}
            aria-label={`${translate('Delivery_fee')}`}
            className="tertiary clear-margins fd-h4"
            style={styles.tertiary}
          >
            {translate('Delivery_fee')}
          </p>
          <Price
            Price={deliveryFee}
            Currency={currency}
            Languages={Languages}
            AppDesignScheme={AppDesignScheme}
            isDark={isDark}
          />
        </ListGroupItem>
      );
    }

    let onlineProcessingCharge: JSX.Element | null = null;
    if (orderFeesTotal > 0) {
      onlineProcessingCharge = (
        <ListGroupItem className="flex">
          <div tabIndex={0} aria-label={`${translate('Online_ordering_fee')}`}>
            <p
              className="tertiary clear-margins fd-h4"
              style={isDark ? styles.primary : styles.tertiary}
            >
              {translate('Online_ordering_fee')}
            </p>
            {Restaurant.DisplayTax && Restaurant.ProcessingFeeTaxRate > 0 ? (
              <p
                className="tertiary clear-margins fd-h5"
                style={isDark ? styles.primary : styles.tertiary}
              >
                (
                {translate('Includes_tax_of', {
                  tax: `${Restaurant.ProcessingFeeTaxRate}%`
                })}
                )
              </p>
            ) : null}
          </div>
          <Price
            Price={orderFeesTotal}
            Currency={currency}
            Languages={Languages}
            AppDesignScheme={AppDesignScheme}
            isDark={isDark}
          />
        </ListGroupItem>
      );
    }

    let basketTaxes = 0;

    for (let item of Basket.items) {
      basketTaxes += item.Item.TaxValue;

      for (let option of item.Options) {
        basketTaxes += option.TaxValue;
      }
    }

    const taxes = (Restaurant.DeliveryFeeTaxAmount || 0) + basketTaxes;
    let taxesRow: JSX.Element | null = null;
    if (Restaurant.DisplayTax) {
      taxesRow = (
        <ListGroupItem className="flex">
          <div tabIndex={0} aria-label={`${translate('Tax')}`}>
            <p
              className="tertiary clear-margins fd-h4"
              style={isDark ? styles.primary : styles.tertiary}
            >
              {translate('Tax')}
            </p>
          </div>
          <Price
            Price={taxes}
            Currency={currency}
            Languages={Languages}
            AppDesignScheme={AppDesignScheme}
            isDark={isDark}
          />
        </ListGroupItem>
      );
    }

    const tipEnabled = this.isTipEnabled();
    const usingTipablePaymentMethod = this.isTipablePaymentMethod();
    let totalToPay = orderTotalToPay;
    const tipAmount = orderTipsTotal;

    let tipRow: JSX.Element | null = null;
    if (tipEnabled && usingTipablePaymentMethod) {
      tipRow = (
        <>
          <ListGroupItem className="flex">
            <div
              tabIndex={0}
              aria-label={`${translate('Tip_{0}', {
                PercentageTip: `${PercentageTip}%`
              })}`}
            >
              <p
                className="tertiary clear-margins fd-h4"
                style={isDark ? styles.primary : styles.tertiary}
              >
                (
                {translate('Tip_{0}', {
                  PercentageTip: `${PercentageTip}%`
                })}
                )
              </p>
            </div>
            <Price
              Price={tipAmount}
              Currency={currency}
              Languages={Languages}
              AppDesignScheme={AppDesignScheme}
              isDark={isDark}
            />
          </ListGroupItem>
          {Customer.status === 'success' && (
            <TipOptions
              translate={translate}
              onChange={this.props.SetTip}
              tipPercentageOptions={Customer.data.TipPercentageOptions}
              selectedTipPercentage={PercentageTip}
              designScheme={AppDesignScheme}
            />
          )}
        </>
      );
    }

    const { type, vrid, id, concessionStoreId } = params;
    const extraRouteParams = concessionStoreId ? `/${concessionStoreId}` : '';
    const backRoute = `/restaurant/${vrid}/${type}/${id}${extraRouteParams}`;

    //Convert array into an object with number of occurences
    const counts: any = {};

    for (let i = 0; i < Basket.items.length; i++) {
      const item = Basket.items[i];

      const key = item.Item.Name + item.Options.map((o) => o.Name).join(', ');

      if (counts[key]) {
        counts[key].occurence = counts[key].occurence ? counts[key].occurence + 1 : 1;
        counts[key].index = i;
        counts[key].item = item;
      } else {
        counts[key] = { occurence: 1, index: i, item };
      }
    }

    //Map object to an array of JSX elements
    const itemList: JSX.Element[] = [];

    for (const key in counts) {
      if (!counts.hasOwnProperty(key)) {
        continue;
      }

      const occurence: number = counts[key].occurence;
      const index: number = counts[key].index;
      const basketItem: BasketControl.Item = counts[key].item;
      const name: string = basketItem.Item.Name;
      const options: string = basketItem.Options.map((o) => o.Name).join(', ');
      const price: number = BasketControl.ItemTotal(basketItem);

      const subtractItem = () => {
        Track('Orders', 'Remove Item From Basket Via Checkout', {
          eventLabel: basketItem.Item.Name,
          virtualRestaurantId: params.vrid,
          menuId: this.props.menu.MenuId
        });
        RemoveItemFromBasket({ menuId: this.props.menu.MenuId, index });
      };

      const addItem = () => {
        Track('Orders', 'Add Item To Basket Via Checkout', {
          eventLabel: basketItem.Item.Name,
          virtualRestaurantId: params.vrid,
          name: basketItem.Item.Name,
          itemType: basketItem.Options.length === 0 ? 'simple' : 'complex',
          menuId: this.props.menu.MenuId
        });

        AddItemToBasket(basketItem);
      };

      const optionsList = options.split(',').map((option, index) => (
        <li key={index} className="clear-margins list-unstyled">
          <p
            className="tertiary-light clear-margins fd-h4"
            style={isDark ? styles.primary : styles.tertiaryLight}
          >
            {option}
          </p>
        </li>
      ));

      const item: JSX.Element = (
        <ListGroupItem key={key} className="flex">
          <div tabIndex={0} aria-label={`${occurence} ${name}`}>
            <p
              className="tertiary clear-margins fd-h3"
              style={isDark ? styles.primary : styles.tertiary}
            >
              {`${occurence} x ${name}`}
            </p>
            {options && <ul className="list-unstyled checkout__items__options">{optionsList}</ul>}
          </div>
          <div className="text-center checkout__items__item-price" style={styles.price}>
            <p
              className="inline-block secondary clear-margins bold fd-h4"
              style={isDark ? styles.primary : styles.secondary}
            >
              {BasketControl.ToCurrency(price * occurence, currency, initialLang)}
            </p>
            <div className="flex flex-justify-center flex-align-center checkout__items__item-price__controls">
              <button
                style={{
                  background: 'none',
                  color: 'inherit',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer'
                }}
                tabIndex={0}
                onClick={subtractItem}
                aria-label={`${translate('Remove')} ${name}`}
              >
                <i
                  key={`minus-icon-${key}`}
                  className="icon_minus_alt2"
                  style={isDark ? (styles.darkControl as any) : (styles.control as any)}
                />
              </button>
              <p
                className="tertiary clear-margins fd-h4"
                style={isDark ? styles.primary : styles.tertiary}
              >
                {occurence}
              </p>
              <button
                style={{
                  background: 'none',
                  color: 'inherit',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer'
                }}
                tabIndex={0}
                onClick={addItem}
                aria-label={`${translate('Add')} ${name}`}
              >
                <i
                  key={`plus-icon-${key}`}
                  className="icon_plus_alt2"
                  style={isDark ? (styles.darkControl as any) : (styles.control as any)}
                />
              </button>
            </div>
          </div>
        </ListGroupItem>
      );

      itemList.push(item);
    }

    const { Category } = AppConfig.data;

    let categoryNoteTranslation = '';
    let categoryNoteExample = translate('Notes_example') as string;

    switch (Category) {
      case 0:
        categoryNoteTranslation = 'Chef';
        categoryNoteExample = translate('Notes_example') as string;
        break;
      case 1:
        categoryNoteTranslation = 'Barista';
        categoryNoteExample = translate('Notes_example') as string;
        break;
      case 2:
        categoryNoteTranslation = 'Store_lowercase';
        categoryNoteExample = translate('Notes_example') as string;
        break;
      default:
        categoryNoteTranslation = 'Chef';
        categoryNoteExample = translate('Notes_example') as string;
    }

    const tableServiceIndex =
      Restaurant.DeliveryType === Api.DeliveryType.Collection &&
      Restaurant.PickupLocationTypes.findIndex(
        (PickupLocation) => PickupLocation === Api.PickupLocationType.TableService
      );

    const dineInIndex =
      Restaurant.DeliveryType === Api.DeliveryType.Collection &&
      Restaurant.PickupLocationTypes.findIndex(
        (PickupLocation) => PickupLocation === Api.PickupLocationType.DineIn
      );

    const takeOutIndex =
      Restaurant.DeliveryType === Api.DeliveryType.Collection &&
      Restaurant.PickupLocationTypes.findIndex(
        (PickupLocation) => PickupLocation === Api.PickupLocationType.TakeOut
      );

    const isTableServiceOnly =
      Restaurant.DeliveryType === Api.DeliveryType.Collection &&
      Restaurant.PickupLocationTypes.length === 1 &&
      tableServiceIndex != -1;

    const isDineInAndTakeOut = dineInIndex != -1 && takeOutIndex != -1;

    const isTableServiceAndTakeOut = tableServiceIndex != -1 && takeOutIndex != -1;

    const TableServiceCategory =
      Restaurant.DeliveryType === Api.DeliveryType.Collection
        ? Restaurant.TableServiceCatagory || Api.TableServiceCategory.Generic
        : Api.TableServiceCategory.Generic;

    const PreOrder = !Restaurant.IsOpen && Restaurant.AcceptPreOrders;

    const AllowPreOrdersAndTableService =
      Restaurant.DeliveryType === Api.DeliveryType.Collection
        ? Restaurant.AllowPreOrdersAndTableService || true
        : true;

    const disabled =
      !!this.props.checkoutValidationErrors.length ||
      (PreOrder &&
        !AllowPreOrdersAndTableService &&
        PickupLocationType === Api.PickupLocationType.TableService);

    const missingTime = this.props.checkoutValidationErrors.includes('requested_time');
    const missingName = this.props.checkoutValidationErrors.includes('name');
    const missingTableNumber = this.props.checkoutValidationErrors.includes('table_number');

    return (
      <div className="container-fluid flex flex-column">
        <TableSelectModal
          ShowModal={this.state.ShowModal}
          Close={this.toggleModal}
          isDark={isDark}
          offset={Offset}
          number={TableNumber}
          handleNumber={this.handleNumberChange}
          handleSubmit={this.handleSubmit}
          PlaceOrderAfterTableSelect={false}
          translate={translate}
          TableServiceCategory={TableServiceCategory}
          PickupLocationOptions={
            Restaurant.DeliveryType === Api.DeliveryType.Collection
              ? Restaurant.PickupLocationOptions
              : null
          }
          AppDesignScheme={AppDesignScheme}
          prid={this.props.params.id}
        />

        <div className="container xs-full-width">
          <Toast Messages={this.props.ToastMessages} AppDesignScheme={AppDesignScheme} />
          <div className="row checkout">
            <div className="col-md-12">
              <div style={{ paddingBottom: '20px' }} />

              <PageTitle>{translate('Checkout')}</PageTitle>

              <RouterBackLink to={backRoute}>{translate('Back')}</RouterBackLink>
            </div>
            <div className="col-md-8 col-lg-8 col-sm-12">
              <Preorder
                AppConfig={AppConfig}
                Restaurant={Restaurant}
                DeliveryLocation={DeliveryLocation}
                PickupLocationType={PickupLocationType}
                RequestedDeliveryOrPickupTimeUtc={this.props.requestedTimeOrDefault}
                ChangeDeliveryTime={this.changeDeliveryTime}
                AppDesignScheme={AppDesignScheme}
                isDark={isDark}
                type={params.type}
                disabled={disabled || loading}
              />

              <div
                className="fd-subheader flex flex-align-center"
                style={isDark ? styles.darkSubheader : styles.subheader}
              >
                <i
                  className="icon_cart secondary"
                  aria-hidden="true"
                  style={isDark ? styles.primary : styles.secondary}
                />
                <h2
                  className="inline-block secondary clear-margins"
                  style={isDark ? styles.primary : styles.secondary}
                >
                  {translate('Your_basket')}
                </h2>
                <p
                  className="secondary clear-margins bold fd-subheader__price fd-h2"
                  style={isDark ? styles.primary : styles.secondary}
                >
                  {BasketControl.ToCurrency(totalToPay, currency, initialLang)}
                </p>
              </div>

              <FormGroup className="checkout__items">
                {itemList}
                {onlineProcessingCharge}
                {deliveryFeeRow}
                {taxesRow}
                {tipRow}
              </FormGroup>

              <Voucher orderPayload={this.props.orderPayload}>
                {this.renderVoucher(currency)}
              </Voucher>

              <hr style={styles.hr} />

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormGroup style={{ marginBottom: 0 }}>
                    <label
                      htmlFor="checkoutName"
                      className="checkout__name tertiary clear-margin-top"
                      style={isDark ? styles.primary : styles.tertiary}
                    >
                      <UserNameLabelText>
                        {({ label, description }) => `${label} (${description})`}
                      </UserNameLabelText>
                    </label>
                    <input
                      id="checkoutName"
                      value={this.props.UserName}
                      onChange={(e) => {
                        this.props.setUserName({ name: e.currentTarget.value });
                      }}
                      style={isDark ? styles.darkFormControl : styles.formControl}
                      className="form-control"
                      key="checkout-name"
                      data-fd="checkout-name"
                    />
                  </FormGroup>
                </Grid>

                {Restaurant.AllowChefNotes && (
                  <Grid item xs={12}>
                    <FormGroup style={{ marginBottom: 0 }}>
                      <label
                        htmlFor="notesForChef"
                        className="tertiary clear-margins-top"
                        style={isDark ? styles.primary : styles.tertiary}
                      >
                        {translate('Notes_for_the_chef', {
                          categoryNote: translate(categoryNoteTranslation) as string
                        })}
                      </label>
                      <textarea
                        id="notesForChef"
                        className="form-control"
                        value={this.chefNote()}
                        onChange={this.handleChefNoteChange}
                        placeholder={categoryNoteExample}
                        style={isDark ? styles.darkFormControl : styles.formControl}
                        data-fd="checkout-notes"
                      />
                    </FormGroup>
                  </Grid>
                )}

                {params.type === 'delivery' ? null : isTableServiceOnly ? (
                  <Grid item xs={12}>
                    <button
                      className="checkout__select-table fd-button fd-button--big fd-button--primary flex flex-align-center flex-justify-center block-center"
                      onClick={this.toggleModal}
                      key="table-select-button"
                      style={
                        [
                          styles.buttonPrimary,
                          Radium.getState(this.state, 'table-select-button', ':active')
                            ? styles.buttonActive
                            : Radium.getState(this.state, 'table-select-button', ':hover')
                            ? styles.buttonPrimaryHover
                            : null
                        ] as any
                      }
                    >
                      <p
                        className="clear-margins secondary fd-h4"
                        style={
                          Radium.getState(this.state, 'table-select-button', ':hover') ||
                          Radium.getState(this.state, 'table-select-button', ':active')
                            ? styles.primary
                            : styles.secondary
                        }
                      >
                        {typeof TableNumber !== 'number'
                          ? translate(`Select_table_${TableServiceCategory}`)
                          : Restaurant.DeliveryType === Api.DeliveryType.Collection &&
                            Restaurant.PickupLocationOptions &&
                            Restaurant.PickupLocationOptions.length > 0
                          ? Restaurant.PickupLocationOptions.find(
                              (option) => option.PickupLocationOptionId === TableNumber
                            ).Value ||
                            `${translate(`Table_${TableServiceCategory}`)} ${TableNumber}`
                          : `${translate(`Table_${TableServiceCategory}`)} ${TableNumber}`}
                      </p>
                    </button>
                  </Grid>
                ) : isDineInAndTakeOut ? (
                  <Grid item xs={12}>
                    <div
                      className="flex checkout__options"
                      style={{
                        marginBottom: 0
                      }}
                    >
                      <button
                        className={`fd-button fd-button--big fd-button--${
                          PickupLocationType === Api.PickupLocationType.DineIn
                            ? 'secondary'
                            : 'primary'
                        } flex flex-align-center flex-justify-center flex-one`}
                        onClick={() => {
                          EditPickupLocationTypes(params.vrid, Api.PickupLocationType.DineIn);
                        }}
                        key="table-select-button"
                        style={
                          [
                            PickupLocationType === Api.PickupLocationType.DineIn
                              ? styles.buttonSecondary
                              : styles.buttonPrimary,
                            Radium.getState(this.state, 'table-select-button', ':active')
                              ? styles.buttonActive
                              : Radium.getState(this.state, 'table-select-button', ':hover')
                              ? PickupLocationType === Api.PickupLocationType.DineIn
                                ? styles.buttonHover
                                : styles.buttonPrimaryHover
                              : null
                          ] as any
                        }
                      >
                        <p
                          className={`clear-margins fd-h4 ${
                            PickupLocationType === Api.PickupLocationType.DineIn
                              ? 'primary'
                              : 'secondary'
                          }`}
                          style={
                            PickupLocationType === Api.PickupLocationType.DineIn
                              ? styles.primary
                              : Radium.getState(this.state, 'table-select-button', ':hover') ||
                                Radium.getState(this.state, 'table-select-button', ':active')
                              ? styles.primary
                              : styles.secondary
                          }
                        >
                          {TableServiceCategory === 7
                            ? translate('Gate_delivery')
                            : translate('Dine_in')}
                        </p>
                      </button>
                      <button
                        className={`fd-button fd-button--big fd-button--${
                          PickupLocationType === Api.PickupLocationType.TakeOut
                            ? 'secondary'
                            : 'primary'
                        } flex flex-align-center flex-justify-center flex-one`}
                        onClick={() => {
                          EditPickupLocationTypes(params.vrid, Api.PickupLocationType.TakeOut);
                        }}
                        key="table-select-button-two"
                        style={
                          [
                            PickupLocationType === Api.PickupLocationType.TakeOut
                              ? styles.buttonSecondary
                              : styles.buttonPrimary,
                            Radium.getState(this.state, 'table-select-button-two', ':active')
                              ? styles.buttonActive
                              : Radium.getState(this.state, 'table-select-button-two', ':hover')
                              ? PickupLocationType === Api.PickupLocationType.DineIn
                                ? styles.buttonHover
                                : styles.buttonPrimaryHover
                              : null
                          ] as any
                        }
                      >
                        <p
                          className={`clear-margins fd-h4 ${
                            PickupLocationType === Api.PickupLocationType.TakeOut
                              ? 'primary'
                              : 'secondary'
                          }`}
                          style={
                            PickupLocationType === Api.PickupLocationType.TakeOut
                              ? styles.primary
                              : Radium.getState(this.state, 'table-select-button-two', ':hover') ||
                                Radium.getState(this.state, 'table-select-button-two', ':active')
                              ? styles.primary
                              : styles.secondary
                          }
                        >
                          {translate('Take_out')}
                        </p>
                      </button>
                    </div>
                  </Grid>
                ) : isTableServiceAndTakeOut ? (
                  <Grid item xs={12}>
                    <div
                      className="flex checkout__options"
                      style={{
                        marginBottom: 0
                      }}
                    >
                      <button
                        className={`fd-button fd-button--big fd-button--${
                          PickupLocationType === Api.PickupLocationType.TableService
                            ? 'secondary'
                            : 'primary'
                        } flex flex-align-center flex-justify-center flex-one`}
                        onClick={() => {
                          EditPickupLocationTypes(params.vrid, Api.PickupLocationType.TableService);
                          this.toggleModal();
                        }}
                        key="table-select-button"
                        style={
                          [
                            PickupLocationType === Api.PickupLocationType.TableService
                              ? styles.buttonSecondary
                              : styles.buttonPrimary,
                            Radium.getState(this.state, 'table-select-button', ':active')
                              ? styles.buttonActive
                              : Radium.getState(this.state, 'table-select-button', ':hover')
                              ? PickupLocationType === Api.PickupLocationType.TableService
                                ? styles.buttonHover
                                : styles.buttonPrimaryHover
                              : null
                          ] as any
                        }
                      >
                        <p
                          className={`clear-margins fd-h5 ${
                            PickupLocationType === Api.PickupLocationType.TableService
                              ? 'primary'
                              : 'secondary'
                          }`}
                          style={
                            PickupLocationType === Api.PickupLocationType.TableService
                              ? styles.primary
                              : Radium.getState(this.state, 'table-select-button', ':hover') ||
                                Radium.getState(this.state, 'table-select-button', ':active')
                              ? styles.primary
                              : styles.secondary
                          }
                        >
                          {TableServiceCategory === 7
                            ? translate('Gate_delivery')
                            : translate('Dine_in')}
                        </p>
                        <p
                          className={`clear-margins fd-h4 ${
                            PickupLocationType === Api.PickupLocationType.TableService
                              ? 'primary'
                              : 'secondary'
                          }`}
                          style={
                            PickupLocationType === Api.PickupLocationType.TableService
                              ? styles.primary
                              : Radium.getState(this.state, 'table-select-button', ':hover') ||
                                Radium.getState(this.state, 'table-select-button', ':active')
                              ? styles.primary
                              : styles.secondary
                          }
                        >
                          {typeof TableNumber !== 'number'
                            ? translate(`Select_table_${TableServiceCategory}`)
                            : Restaurant.DeliveryType === Api.DeliveryType.Collection &&
                              Restaurant.PickupLocationOptions &&
                              Restaurant.PickupLocationOptions.length > 0
                            ? Restaurant.PickupLocationOptions.find(
                                (option) => option.PickupLocationOptionId === TableNumber
                              ).Value ||
                              `${translate(`Table_${TableServiceCategory}`)} ${TableNumber}`
                            : `${translate(`Table_${TableServiceCategory}`)} ${TableNumber}`}
                        </p>
                      </button>
                      <button
                        className={`fd-button fd-button--big fd-button--${
                          PickupLocationType === Api.PickupLocationType.TakeOut
                            ? 'secondary'
                            : 'primary'
                        } flex flex-align-center flex-justify-center flex-one`}
                        onClick={() => {
                          EditPickupLocationTypes(params.vrid, Api.PickupLocationType.TakeOut);
                        }}
                        key="table-select-button-two"
                        style={
                          [
                            PickupLocationType === Api.PickupLocationType.TakeOut
                              ? styles.buttonSecondary
                              : styles.buttonPrimary,
                            Radium.getState(this.state, 'table-select-button-two', ':active')
                              ? styles.buttonActive
                              : Radium.getState(this.state, 'table-select-button-two', ':hover')
                              ? PickupLocationType === Api.PickupLocationType.DineIn
                                ? styles.buttonHover
                                : styles.buttonPrimaryHover
                              : null
                          ] as any
                        }
                      >
                        <p
                          className={`clear-margins fd-h5 ${
                            PickupLocationType === Api.PickupLocationType.TakeOut
                              ? 'primary'
                              : 'secondary'
                          }`}
                          style={
                            PickupLocationType === Api.PickupLocationType.TakeOut
                              ? styles.primary
                              : Radium.getState(this.state, 'table-select-button-two', ':hover') ||
                                Radium.getState(this.state, 'table-select-button-two', ':active')
                              ? styles.primary
                              : styles.secondary
                          }
                        >
                          {translate('Take_out')}
                        </p>
                        <p
                          className={`clear-margins fd-h4 ${
                            PickupLocationType === Api.PickupLocationType.TakeOut
                              ? 'primary'
                              : 'secondary'
                          }`}
                          style={
                            PickupLocationType === Api.PickupLocationType.TakeOut
                              ? styles.primary
                              : Radium.getState(this.state, 'table-select-button-two', ':hover') ||
                                Radium.getState(this.state, 'table-select-button-two', ':active')
                              ? styles.primary
                              : styles.secondary
                          }
                        >
                          {translate('Collect_counter')}
                        </p>
                      </button>
                    </div>
                  </Grid>
                ) : null}

                {this.props.checkoutError && (
                  <Grid item xs={12}>
                    <Alert color="error">
                      {translate(this.props.checkoutError.message, undefined, {
                        missingTranslationMsg: this.props.checkoutError.message
                      })}
                    </Alert>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <PaymentSection
                    payments={this.props.Payments}
                    selectedPayment={this.props.Account}
                    translate={translate}
                    loading={loading}
                    disabled={disabled}
                    onSubmit={this.handleSubmit}
                  />
                </Grid>
              </Grid>
            </div>
          </div>
        </div>
        <PermissionConsentDialog />

        <SelectTimeModal
          show={missingTime || missingName || missingTableNumber}
          selectedPayment={this.props.Account}
          loading={loading}
          disabled={false}
          translate={this.props.translate}
          onOk={this.props.continueCheckoutFlow}
          onCancel={this.props.cancelCheckoutFlow}
          preOrderTimeProps={
            missingTime && {
              isDelivery: params.type == 'delivery',
              value: this.props.requestedTimeOrDefault,
              options:
                Restaurant &&
                Restaurant.PreOrderOptions &&
                Restaurant.PreOrderOptions.PreOrderTimes,
              onChange: (time: string) => {
                this.props.SetRequestedTime({
                  time,
                  menuId: this.props.menu.MenuId
                });
              },
              translate: this.props.translate
            }
          }
          nameInputProps={
            missingName && {
              value: this.props.UserName,
              onChange: (name: string) => {
                this.props.setUserName({ name });
              }
            }
          }
          tableNumberInputProps={
            missingTableNumber && {
              value: this.props.TableNumber,
              category: TableServiceCategory,
              options:
                Restaurant.DeliveryType === Api.DeliveryType.Collection
                  ? Restaurant.PickupLocationOptions
                  : undefined,
              onChange: (tableNumber?: number) => {
                this.props.EditTableNumbers(params.vrid, tableNumber);
              },
              translate: this.props.translate
            }
          }
        />
      </div>
    );
  }
}

const mapStateToProps = (state: Store.State, ownProps: Routes.Routes) => {
  const deliveryType = ownProps.params.type;
  const isDelivery = deliveryType === 'delivery';
  const vrId = ownProps.params.vrid;
  const prId = !isDelivery ? ownProps.params.id : undefined;
  const deliveryLocationId = isDelivery ? ownProps.params.id : undefined;

  const Restaurant = menuSelectors.getRestaurantDetails(state, {
    deliveryType,
    vrId,
    prId,
    deliveryLocationId
  });

  let dl: Api.DeliveryLocation | null = null;
  if (isDelivery && state.flipdish.DeliveryLocations.status === 'success') {
    dl =
      find(
        state.flipdish.DeliveryLocations.data,
        (dl) => dl.DeliveryLocationId == ownProps.params.id
      ) || null;
  }

  const { flipdish } = state;
  const menu = menuSelectors.findMenu(state, {
    vrId,
    deliveryType,
    prId,
    deliveryLocationId
  });

  const basket = basketSelectors.getBasketBridge(state);
  const basketLoaded = basket && basket.items.length ? true : false;

  const tip =
    typeof state.basket.tip === 'number'
      ? state.basket.tip
      : flipdish.Customer.status === 'success'
      ? flipdish.Customer.data.DefaultTipPercentage
      : 0;

  const orderPayload = basketSelectors.getOrderPayload(state);

  const orderTotalToPay = basketSelectors.getTotalToPay(state);

  const orderTipsTotal = basketSelectors.getTipTotal(state);

  const requestedTimeOrDefault = basketSelectors.getSelectedRequestedTimeOrDefault(state);

  const PickupLocationType = basketSelectors.getPickupLocationTypeOrDefault(state);

  const orderFeesTotal = basketSelectors.getFeesTotal(state);

  const Payments = paymentSelectors.getAvailablePayments(state, {
    deliveryType,
    vrId,
    prId,
    deliveryLocationId
  });
  const Account = basketSelectors.getSelectedPaymentOrDefault(state);
  const UserName = flipdish.Customer.status === 'success' && flipdish.Customer.data.CustomerName;

  const loading = CHECKOUT_LOADING_STATES.includes(state.basket.status);

  return {
    AppConfig: state.flipdish.AppConfig,
    Customer: flipdish.Customer,
    UserName,
    Payments,
    Account,
    Basket: basket,
    PercentageTip: tip,
    ChefNotes: basketSelectors.getChefNotes(state),
    TableNumber: basketSelectors.getPickupLocationIdOrDefault(state),
    PickupLocationType,
    DeliveryLocation: dl,
    Order: state.flipdish.Order,
    deviceId: state.flipdish.Config.DeviceId,
    location: state.flipdish.Location.geo,
    Restaurant,
    Menus: state.flipdish.MenuData,
    translate: getTranslate(state.locale),
    Languages: state.locale.languages,
    ToastMessages: state.flipdish.ToastMessages,
    VouchersInProgress: state.flipdish.VouchersInProgress,
    Offset: state.flipdish.Offset,
    isDark: state.flipdish.isDark,
    AppDesignScheme: state.appDesignScheme,
    CollectionSummaries: state.flipdish.CollectionSummaries,
    DeliverySummaries: state.flipdish.DeliverySummaries,
    menu,
    basketLoaded,
    orderPayload,
    orderTotalToPay,
    orderTipsTotal,
    orderFeesTotal,
    requestedTimeOrDefault,
    checkoutStatus: state.basket.status,
    checkoutValidationErrors: state.basket.validationErrors,
    checkoutError: state.basket.error,
    loading
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<Store.State, {}, Action>) => {
  return {
    loginSuccessBridge: () => {
      dispatch(userActions.loginSuccessBridge() as any);
    },
    loginErrorBridge: () => {
      dispatch(userActions.loginErrorBridge() as any);
    },
    startCheckoutFlow: () => {
      dispatch(basketActions.startCheckoutFlow());
    },
    continueCheckoutFlow: () => {
      dispatch(basketActions.continueCheckoutFlow());
    },
    cancelCheckoutFlow: () => {
      dispatch(basketActions.cancelCheckoutFlow());
    },
    setUserName: (payload: { name: string }) => {
      dispatch({
        type: 'SET_CUSTOMER_NAME',
        payload
      });
    },

    Submit: (
      order: Api.DeliveryOrder | Api.CollectionOrder,
      vrid: number,
      account: Api.PaymentAccount,
      voucherCode: string,
      chefNote: string,
      basket: BasketControl.Data,
      currency: Api.Currency,
      total: number,
      type?: 'STRIPE' | 'NORMAL' | 'PAYPAL',
      options?: SubmitTypeOptions,
      concessionStoreId?: number
    ) => {
      dispatch(
        Actions.SubmitOrder(
          order,
          vrid,
          account,
          voucherCode,
          chefNote,
          basket,
          currency,
          total,
          type,
          options,
          concessionStoreId
        )
      );
    },
    LoadRestaurant: (routes: Routes.Routes) => {
      dispatch(Actions.LoadRestaurant(routes));
    },

    AddItemToBasket: (payload: BasketControl.Item) => {
      dispatch(Actions.AddToBasket(payload));
    },
    RemoveItemFromBasket: (payload: { menuId: number; index: number; indexName?: string }) => {
      dispatch(Actions.RemoveFromBasket(payload));
    },
    SetTip: (payload: number) => {
      dispatch(Actions.SetTip(payload));
    },

    EditChefNotes: (vrid: number, value: string) => {
      dispatch(Actions.EditChefNotes(vrid, value));
    },

    EditTableNumbers: (vrid: number, value: number) => {
      dispatch(Actions.EditTableNumbers(vrid, value));
    },

    EditPickupLocationTypes: (vrid: number, value: Api.PickupLocationType) => {
      dispatch(Actions.EditPickupLocationTypes(vrid, value));
    },

    SetRequestedTime: (payload: { menuId: number; time: string }) => {
      dispatch(Actions.SetRequestedTime(payload));
    },

    AddToast: (message: string) => {
      dispatch(Actions.AddToast(message));
    },
    SetOrder: (order: OrderFlow.State) => {
      dispatch(Actions.SetOrder(order));
    },
    FindInitialRestaurant: (): any => {
      dispatch(Actions.FindInitialRestaurant());
    },
    SetCustomer: (customer: Data.Async<Api.Customer>) => {
      dispatch({
        type: 'SET_CUSTOMER',
        payload: customer
      });
      localStorage.removeItem('flipdish-customer');
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Radium(Checkout));
